import React, { useEffect, useMemo, useState } from "react";
import { useParams, useHistory } from "react-router-dom";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import CustomerLayout from "../CustomerLayout";
import { API } from "../../../config";

function getToken() {
  return (
    localStorage.getItem("customer_token") ||
    localStorage.getItem("token")
  );
}

function money(cents) {
  return `$${(Number(cents || 0) / 100).toFixed(2)}`;
}

function normalizeOfferRole(role) {
  const value = String(role || "").toLowerCase();

  if (value.includes("customer")) {
    return "customer";
  }

  if (
    value.includes("courier") ||
    value.includes("driver") ||
    value.includes("carrier")
  ) {
    return "carrier";
  }

  return value;
}

function isPendingStatus(status) {
  return String(status || "").toUpperCase() === "PENDING";
}

function isAcceptedStatus(status) {
  return String(status || "").toUpperCase() === "ACCEPTED";
}

function isRejectedStatus(status) {
  return String(status || "").toUpperCase() === "REJECTED";
}

function isCustomerOffer(offer) {
  return normalizeOfferRole(offer?.proposer_role) === "customer";
}

function isCarrierOffer(offer) {
  return normalizeOfferRole(offer?.proposer_role) === "carrier";
}

function offerTitleForCustomer(offer) {
  return isCustomerOffer(offer) ? "Your Offer" : "Driver / Carrier Offer";
}

function messageSenderLabel(role) {
  const normalized = normalizeOfferRole(role);

  if (normalized === "customer") {
    return "Customer";
  }

  if (normalized === "carrier") {
    return "Driver / Carrier";
  }

  return "User";
}

function offerStatusChipColor(status) {
  if (isPendingStatus(status)) {
    return "warning";
  }

  if (isAcceptedStatus(status)) {
    return "success";
  }

  if (isRejectedStatus(status)) {
    return "error";
  }

  return "default";
}

const NegotiationThread = () => {
  const { threadId } = useParams();
  const history = useHistory();

  const [messages, setMessages] = useState([]);
  const [offers, setOffers] = useState([]);
  const [threadInfo, setThreadInfo] = useState(null);

  const [msgBody, setMsgBody] = useState("");
  const [offerAmount, setOfferAmount] = useState("");
  const [offerNotes, setOfferNotes] = useState("");

  const [err, setErr] = useState("");
  const [loading, setLoading] = useState(true);

  const pendingOffer = useMemo(
    () => offers.find((offer) => isPendingStatus(offer.status)),
    [offers]
  );

  const pendingOfferFromCustomer = Boolean(
    pendingOffer && isCustomerOffer(pendingOffer)
  );

  const pendingOfferFromCarrier = Boolean(
    pendingOffer && isCarrierOffer(pendingOffer)
  );

  const offerInputsDisabled = Boolean(
    pendingOffer && !pendingOfferFromCarrier
  );

  const loadLabel =
    threadInfo?.order_number ||
    (threadInfo?.order_id ? `ORD-${threadInfo.order_id}` : "");

  const carrierLabel = threadInfo?.courier_name || "";

  const loadAll = async () => {
    try {
      setErr("");

      // Load thread summary so the customer sees the real load/order number.
      // This is intentionally non-blocking: messages/offers still load if summary fails.
      try {
        const summaryRes = await fetch(
          `${API}/api/negotiations/thread/${threadId}`,
          {
            headers: {
              Authorization: `Bearer ${getToken()}`,
            },
          }
        );

        if (summaryRes.ok) {
          const summary = await summaryRes.json();
          setThreadInfo(summary);
        }
      } catch (summaryError) {
        console.warn(
          "Unable to load negotiation thread summary:",
          summaryError
        );
      }

      const [mRes, oRes] = await Promise.all([
        fetch(`${API}/api/negotiations/thread/${threadId}/messages`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }),
        fetch(`${API}/api/negotiations/thread/${threadId}/offers`, {
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }),
      ]);

      if (!mRes.ok) {
        throw new Error(`Load messages failed (${mRes.status})`);
      }

      if (!oRes.ok) {
        throw new Error(`Load offers failed (${oRes.status})`);
      }

      const messagesData = await mRes.json();
      const offersData = await oRes.json();

      setMessages(
        Array.isArray(messagesData)
          ? messagesData
          : messagesData.messages || messagesData.rows || []
      );

      setOffers(
        Array.isArray(offersData)
          ? offersData
          : offersData.offers || offersData.rows || []
      );
    } catch (e) {
      setErr(e.message || "Error loading negotiation");
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);

    const initialize = async () => {
      setLoading(true);
      await loadAll();
      setLoading(false);
    };

    initialize();

    const timer = setInterval(() => {
      loadAll();
    }, 6000);

    return () => clearInterval(timer);

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [threadId]);

  const sendMessage = async () => {
    try {
      setErr("");

      const body = msgBody.trim();

      if (!body) {
        return;
      }

      const res = await fetch(
        `${API}/api/negotiations/thread/${threadId}/messages`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            body,
            message: body,
            content: body,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Send message failed (${res.status})`);
      }

      setMsgBody("");
      await loadAll();
    } catch (e) {
      setErr(e.message || "Error sending message");
    }
  };

  const createOffer = async () => {
    try {
      setErr("");

      if (pendingOfferFromCustomer) {
        setErr(
          "Your offer is already pending. Wait for the driver / carrier to respond."
        );
        return;
      }

      if (pendingOfferFromCarrier) {
        setErr(
          "A driver / carrier offer is pending. Enter a counter amount and press Counter on that offer."
        );
        return;
      }

      const dollars = Number(offerAmount);

      if (!Number.isFinite(dollars) || dollars <= 0) {
        setErr("Enter a valid offer amount.");
        return;
      }

      const amount_cents = Math.round(dollars * 100);
      const noteText = offerNotes.trim() || null;

      const res = await fetch(
        `${API}/api/negotiations/thread/${threadId}/offers`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount_cents,
            amountCents: amount_cents,
            amount: amount_cents,
            notes: noteText,
            note: noteText,
            message: noteText,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Create offer failed (${res.status})`);
      }

      setOfferAmount("");
      setOfferNotes("");
      await loadAll();
    } catch (e) {
      setErr(e.message || "Error creating offer");
    }
  };

  const acceptOffer = async (offerId) => {
    try {
      setErr("");

      const res = await fetch(
        `${API}/api/negotiations/offers/${offerId}/accept`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Accept failed (${res.status})`);
      }

      alert("Offer accepted. Price locked and driver / carrier assigned.");
      await loadAll();

      // Option: go back to order history after acceptance.
      // history.push("/customer/order-history");
    } catch (e) {
      setErr(e.message || "Error accepting offer");
    }
  };

  const rejectOffer = async (offerId) => {
    try {
      setErr("");

      const res = await fetch(
        `${API}/api/negotiations/offers/${offerId}/reject`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
          },
        }
      );

      if (!res.ok) {
        throw new Error(`Reject failed (${res.status})`);
      }

      await loadAll();
    } catch (e) {
      setErr(e.message || "Error rejecting offer");
    }
  };

  const counterOffer = async (offerId) => {
    try {
      setErr("");

      const dollars = Number(offerAmount);

      if (!Number.isFinite(dollars) || dollars <= 0) {
        setErr("Enter a valid counter amount in the Amount box.");
        return;
      }

      const amount_cents = Math.round(dollars * 100);
      const noteText = offerNotes.trim() || null;

      const res = await fetch(
        `${API}/api/negotiations/offers/${offerId}/counter`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${getToken()}`,
            "Content-Type": "application/json",
          },
          body: JSON.stringify({
            amount_cents,
            amountCents: amount_cents,
            amount: amount_cents,
            notes: noteText,
            note: noteText,
            message: noteText,
          }),
        }
      );

      if (!res.ok) {
        throw new Error(`Counter failed (${res.status})`);
      }

      setOfferAmount("");
      setOfferNotes("");
      await loadAll();
    } catch (e) {
      setErr(e.message || "Error countering offer");
    }
  };

  return (
    <CustomerLayout>
      <Box sx={{ width: "100%", overflowX: "hidden" }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 2.25, sm: 3, md: 4 },
            borderRadius: 3,
            maxWidth: 1100,
            mx: "auto",
            border: "1px solid #e2e8f0",
            boxShadow: {
              xs: "none",
              md: "0 10px 24px rgba(15, 23, 42, 0.06)",
            },
          }}
        >
          <Box sx={{ textAlign: "center", mb: 3 }}>
            <Typography
              variant="h4"
              fontWeight={800}
              gutterBottom
              sx={{ fontSize: { xs: "2rem", md: "2.5rem" } }}
            >
              Negotiation
            </Typography>

            {loadLabel && (
              <Typography variant="body1" sx={{ color: "#0f172a", mt: 1 }}>
                Load {loadLabel}
              </Typography>
            )}

            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Thread #{threadId}
            </Typography>

            {carrierLabel && (
              <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                Driver / Carrier: {carrierLabel}
              </Typography>
            )}
          </Box>

          {err && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {err}
            </Alert>
          )}

          {loading && (
            <Alert severity="info" sx={{ mb: 2 }}>
              Loading negotiation details...
            </Alert>
          )}

          <Grid container spacing={2}>
            <Grid item xs={12} md={8}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fff",
                  height: "100%",
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Chat
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box
                  sx={{
                    height: { xs: 360, md: 420 },
                    overflowY: "auto",
                    border: "1px solid #e2e8f0",
                    borderRadius: 2,
                    backgroundColor: "#f8fafc",
                    p: 2,
                  }}
                >
                  {messages.length === 0 ? (
                    <Box
                      sx={{
                        height: "100%",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        textAlign: "center",
                      }}
                    >
                      <Typography variant="body2" color="text.secondary">
                        No messages yet.
                      </Typography>
                    </Box>
                  ) : (
                    messages.map((message) => (
                      <Box
                        key={message.id}
                        sx={{
                          mb: 1.5,
                          p: 1.5,
                          borderRadius: 2,
                          backgroundColor: "#fff",
                          border: "1px solid #e2e8f0",
                        }}
                      >
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            gap: 1,
                            mb: 0.5,
                          }}
                        >
                          <Typography
                            variant="body2"
                            fontWeight={800}
                            sx={{ color: "#0f172a" }}
                          >
                            {messageSenderLabel(message.sender_role)}
                          </Typography>

                          <Typography variant="caption" color="text.secondary">
                            {message.created_at
                              ? new Date(message.created_at).toLocaleString()
                              : ""}
                          </Typography>
                        </Box>

                        <Typography
                          variant="body2"
                          sx={{
                            color: "#475569",
                            lineHeight: 1.5,
                            whiteSpace: "pre-wrap",
                            wordBreak: "break-word",
                          }}
                        >
                          {message.body || message.message || message.content || ""}
                        </Typography>
                      </Box>
                    ))
                  )}
                </Box>

                <Box
                  sx={{
                    display: "flex",
                    gap: 1,
                    mt: 2,
                  }}
                >
                  <TextField
                    fullWidth
                    size="small"
                    value={msgBody}
                    onChange={(event) => setMsgBody(event.target.value)}
                    placeholder="Type message..."
                    onKeyDown={(event) => {
                      if (event.key === "Enter" && !event.shiftKey) {
                        event.preventDefault();
                        sendMessage();
                      }
                    }}
                  />

                  <Button
                    variant="contained"
                    onClick={sendMessage}
                    sx={{
                      minWidth: 90,
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 2,
                    }}
                  >
                    Send
                  </Button>
                </Box>
              </Paper>
            </Grid>

            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                sx={{
                  p: { xs: 2, md: 2.5 },
                  borderRadius: 2,
                  border: "1px solid #e2e8f0",
                  backgroundColor: "#fff",
                }}
              >
                <Typography variant="h6" fontWeight={800} gutterBottom>
                  Offers
                </Typography>

                <Divider sx={{ mb: 2 }} />

                <Box sx={{ mb: 2 }}>
                  {offers.length === 0 ? (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{ textAlign: "center", py: 2 }}
                    >
                      No offers yet.
                    </Typography>
                  ) : (
                    offers.map((offer) => {
                      const offerId = offer.id || offer.offer_id;
                      const isOwnOffer = isCustomerOffer(offer);
                      const canRespondToOffer =
                        isPendingStatus(offer.status) && isCarrierOffer(offer);

                      return (
                        <Paper
                          key={offerId}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            mb: 1.5,
                            borderRadius: 2,
                            border: "1px solid #e2e8f0",
                            backgroundColor: "#f8fafc",
                          }}
                        >
                          <Box
                            sx={{
                              display: "flex",
                              justifyContent: "space-between",
                              gap: 1,
                              mb: 0.75,
                            }}
                          >
                            <Typography
                              variant="body2"
                              fontWeight={800}
                              sx={{ color: "#0f172a" }}
                            >
                              {offerTitleForCustomer(offer)}
                            </Typography>

                            <Chip
                              label={offer.status || "Unknown"}
                              size="small"
                              color={offerStatusChipColor(offer.status)}
                              variant="outlined"
                              sx={{
                                height: 24,
                                fontSize: "0.72rem",
                                fontWeight: 700,
                              }}
                            />
                          </Box>

                          <Typography
                            variant="h6"
                            fontWeight={800}
                            sx={{ color: "#0f172a", mb: 0.5 }}
                          >
                            {money(offer.amount_cents)}
                          </Typography>

                          {(offer.notes || offer.note || offer.message) && (
                            <Typography
                              variant="body2"
                              sx={{
                                color: "#475569",
                                mb: 1,
                                whiteSpace: "pre-wrap",
                                wordBreak: "break-word",
                              }}
                            >
                              Notes: {offer.notes || offer.note || offer.message}
                            </Typography>
                          )}

                          {isOwnOffer && isPendingStatus(offer.status) && (
                            <Alert severity="info" sx={{ mt: 1 }}>
                              Waiting for the driver / carrier to respond.
                            </Alert>
                          )}

                          {canRespondToOffer && (
                            <Stack
                              direction={{ xs: "column", sm: "row", md: "column" }}
                              spacing={1}
                              sx={{ mt: 1.5 }}
                            >
                              <Button
                                variant="contained"
                                size="small"
                                onClick={() => acceptOffer(offerId)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderRadius: 2,
                                }}
                              >
                                Accept
                              </Button>

                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => rejectOffer(offerId)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderRadius: 2,
                                }}
                              >
                                Reject
                              </Button>

                              <Button
                                variant="outlined"
                                size="small"
                                onClick={() => counterOffer(offerId)}
                                sx={{
                                  textTransform: "none",
                                  fontWeight: 700,
                                  borderRadius: 2,
                                }}
                              >
                                Counter
                              </Button>
                            </Stack>
                          )}
                        </Paper>
                      );
                    })
                  )}
                </Box>

                <Divider sx={{ my: 2 }} />

                <Typography variant="subtitle1" fontWeight={800}>
                  {pendingOfferFromCustomer
                    ? "Your pending offer"
                    : pendingOfferFromCarrier
                      ? "Counter offer"
                      : "Send offer"}
                </Typography>

                {pendingOfferFromCustomer && (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    Your offer is pending. Wait for the driver / carrier to
                    accept, reject, or counter.
                  </Alert>
                )}

                {pendingOfferFromCarrier && (
                  <Alert severity="warning" sx={{ mt: 1 }}>
                    Enter a counter amount and optional notes below, then press
                    Counter on the driver / carrier offer.
                  </Alert>
                )}

                <TextField
                  fullWidth
                  size="small"
                  value={offerAmount}
                  onChange={(event) => setOfferAmount(event.target.value)}
                  placeholder="Amount in dollars (e.g. 145)"
                  disabled={offerInputsDisabled}
                  inputProps={{ inputMode: "decimal" }}
                  sx={{ mt: 1.5 }}
                />

                <TextField
                  fullWidth
                  size="small"
                  value={offerNotes}
                  onChange={(event) => setOfferNotes(event.target.value)}
                  placeholder="Notes (optional)"
                  disabled={offerInputsDisabled}
                  multiline
                  minRows={2}
                  sx={{ mt: 1 }}
                />

                <Button
                  variant="contained"
                  fullWidth
                  onClick={createOffer}
                  disabled={Boolean(pendingOffer)}
                  sx={{
                    mt: 1.5,
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 2,
                  }}
                >
                  {pendingOfferFromCarrier ? "Use Counter Button Above" : "Send Offer"}
                </Button>

                <Button
                  variant="text"
                  fullWidth
                  onClick={() => history.push("/customer/order-history")}
                  sx={{
                    mt: 1,
                    textTransform: "none",
                    fontWeight: 700,
                  }}
                >
                  Back to Order History
                </Button>
              </Paper>
            </Grid>
          </Grid>
        </Paper>
      </Box>
    </CustomerLayout>
  );
};

export default NegotiationThread;